import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { useSession } from "../lib/session";
import { api, ApiFailure } from "../lib/api";
import type { SignupConfigDto } from "@delta/shared";
import { Icon } from "../components/Icon";
import { Wordmark } from "../components/Shell";
import { useToast } from "../components/Toast";
import { agentPairOfferWaiting } from "../lib/agent";
import { DemoStart } from "../components/Demo";

// Ported from the approved prototype's login (app.js renderLogin): same layout, copy and right-hand artwork.
// Differences: the fields start empty, the form signs in against the server, and the prototype note is gone.
export function Login() {
  const { t, lang, setLang } = useLang();
  const { signIn } = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(false);
  const [dest, setDest] = useState<"home" | "pos">("home");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = `${t("signIn")} · Delta`; }, [t]);
  // Public registration is offered only while public signup is open; in Closed mode the link is not shown (and the
  // server refuses a direct request anyway). Until the answer arrives nothing is shown.
  const [signupOpen, setSignupOpen] = useState(false);
  useEffect(() => { api<SignupConfigDto>("GET", "/signup/config").then((c) => setSignupOpen(c.mode !== "closed"), () => setSignupOpen(false)); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^05\d{8}$/.test(phone.trim())) { setError(t("mobileFormat")); return; }
    if (!password) { setError(t("fixRequired")); return; }
    setBusy(true);
    try {
      const m = await signIn(phone.trim(), password, remember);
      // A business that signed up opens its setup wizard until the administrator finishes it.
      // Setup on this PC sent a code to connect its Print Agent: continue there, where it is asked for.
      nav(m.organization.onboardingPending && m.user.administrator ? "/onboarding" : agentPairOfferWaiting() ? "/pos/printers" : dest === "pos" ? "/pos" : "/home", { replace: true });
    } catch (err) {
      const code = err instanceof ApiFailure ? err.code : null;
      setError(code === "too_many_attempts" ? t("tooManyAttempts") : code === "account_pending" ? t("accountPending") : code === "organization_suspended" ? t("orgSuspended")
        : err instanceof ApiFailure && err.status === 401 ? t("signInFailed") : t("serverError"));
    } finally {
      setBusy(false);
    }
  }

  const bars = (hs: number[]) => <div className="art-bars">{hs.map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div>;
  return (
    <div className="login">
      <section className="login-form-col">
        <div className="login-head"><Wordmark light={false} />
          <button className="btn btn-ghost lang-btn" onClick={() => setLang(lang === "ar" ? "en" : "ar")}><Icon name="globe" />{t("switchLang")}</button>
        </div>
        <div className="login-body">
          <h1>{t("signInTitle")}</h1>
          <p className="lede">{t("signInLede")}</p>
          <form id="login-form" noValidate onSubmit={submit}>
            <div className="field"><label htmlFor="m">{t("mobileNumber")}</label>
              <div className="input-group"><span className="lead"><Icon name="phone" /></span>
                <input id="m" className="control num" style={{ textAlign: "start" }} type="tel" inputMode="tel" autoComplete="username" placeholder={t("mobilePh")} value={phone} onChange={(e) => setPhone(e.target.value)} /></div></div>
            <div className="field"><label htmlFor="pw">{t("password")}</label>
              <div className="input-group has-trail"><span className="lead"><Icon name="lock" /></span>
                <input id="pw" className="control" type={show ? "text" : "password"} autoComplete="current-password" placeholder={t("passwordPh")} value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" className="btn btn-ghost btn-sm btn-icon trail" onClick={() => setShow((s) => !s)} aria-label={t("showPw")}><Icon name={show ? "eyeOff" : "eye"} /></button></div></div>
            <div className="login-row">
              <label className="check"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> {t("rememberMe")}</label>
              <span style={{ display: "flex", gap: 14 }}>
                <a href="#" onClick={(e) => { e.preventDefault(); toast(t("forgotNotYet")); }}>{t("resetPassword")}</a>
                <Link to="/recover">{t("haveRecoveryCode")}</Link>
                <a href="#" onClick={(e) => { e.preventDefault(); toast(t("forgotNotYet")); }}>{t("forgetUid")}</a>
              </span>
            </div>
            <div className="field"><span className="label">{t("afterSignIn")}</span>
              <div className="segmented login-dest">
                {([["home", "home", t("destDashboard")], ["pos", "cart", t("destPos")]] as const).map(([v, icn, l]) => (
                  <label key={v}><input type="radio" name="dest" value={v} checked={dest === v} onChange={() => setDest(v)} /><span><Icon name={icn} />{l}</span></label>
                ))}
              </div></div>
            {error && <div className="dlg-alert show" role="alert" data-testid="login-error"><Icon name="alert" /><span>{error}</span></div>}
            <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? t("signingIn") : t("signIn")}</button>
          </form>
          <DemoStart />
          {signupOpen && <p className="login-alt">{t("noAccount")} <Link to="/signup" data-testid="signup-link">{t("register")}</Link></p>}
        </div>
        <div className="login-foot"><span>{t("copyright")}</span><a href="https://deltasa.net" target="_blank" rel="noopener">{t("officialSite")} · deltasa.net</a></div>
      </section>
      <section className="login-art" aria-hidden="true">
        <div className="grid-bg" />
        <div><Wordmark light /></div>
        <div><h2>{t("artTitle")}</h2><p>{t("artBody")}</p></div>
        <div className="art-cards">
          <div className="art-row">
            <div className="art-card"><div className="k">{t("artSales")}</div><div className="v"><span className="num">486,320</span> <small style={{ fontSize: 12, opacity: 0.7 }}>{t("currency")}</small></div>{bars([42, 55, 48, 63, 60, 74, 68, 82, 77, 90])}</div>
            <div className="art-card"><div className="k">{t("artCollected")}</div><div className="v"><span className="num">92.4%</span></div>{bars([70, 72, 76, 74, 81, 85, 83, 88, 90, 92])}</div>
          </div>
          <div className="art-points"><span><Icon name="check" />{t("artPoint1")}</span><span><Icon name="check" />{t("artPoint2")}</span><span><Icon name="check" />{t("artPoint3")}</span></div>
        </div>
      </section>
    </div>
  );
}
